// This file declares the actual data structures that are saved to the disk.
// They look a lot like the data store model, but they only include types that
// map to and from JSON natively.

import * as t from '@/model/types'
import { ifNull, ifUndef, JsonAny } from '@/ts_utils'
import * as z from 'zod'

const task = z.object({
  x: z.number(),
  y: z.number(),
  title: z.string(),
  createdAt: z.number(),
  completedAt: z.number().optional(),
  deletedAt: z.number().optional(),
  duration: z.number().optional(),
  priority: z.number().optional()
})

const dependency = z.object({
  prereq: z.string(),
  dependent: z.string(),
  createdAt: z.number(),
  deletedAt: z.number().optional()
})

const root = z.object({
  version: z.literal(1),
  tasks: z.record(task),
  dependencies: z.record(dependency)
})

function dateToNumber (d: Date): number {
  return d.valueOf()
}

function numberToDate (n: number): Date {
  return new Date(n)
}

export function loadProject (input: string): t.Project {
  const r = root.parse(JSON.parse(input))

  const tasks: Record<t.TaskId, t.Task> = {}
  for (const [id, task] of Object.entries(r.tasks)) {
    tasks[id] = {
      ...task,
      createdAt: numberToDate(task.createdAt),
      completedAt: task.completedAt === undefined ? null : numberToDate(task.completedAt),
      deletedAt: task.deletedAt === undefined ? null : numberToDate(task.deletedAt),
      duration: ifUndef(task.duration, null),
      priority: ifUndef(task.priority, null),
      estimatedCompletion: null
    }
  }

  const dependencies: Record<t.DependencyId, t.Dependency> = {}
  for (const [id, dependency] of Object.entries(r.dependencies)) {
    dependencies[id] = {
      ...dependency,
      createdAt: numberToDate(dependency.createdAt),
      deletedAt: dependency.deletedAt === undefined ? null : numberToDate(dependency.deletedAt)
    }
  }

  return {
    dependencies,
    tasks
  }
}

export function saveProject (project: t.Project): string {
  const tasks: Record<string, z.infer<typeof task>> = {}
  for (const [id, task] of Object.entries(project.tasks)) {
    tasks[id] = {
      x: task.x,
      y: task.y,
      title: task.title,
      createdAt: dateToNumber(task.createdAt),
      completedAt: task.completedAt === null ? undefined : dateToNumber(task.completedAt),
      deletedAt: task.deletedAt === null ? undefined : dateToNumber(task.deletedAt),
      duration: ifNull(task.duration, undefined),
      priority: ifNull(task.priority, undefined)
    }
  }

  const dependencies: Record<string, z.infer<typeof dependency>> = {}
  for (const [id, dependency] of Object.entries(project.dependencies)) {
    dependencies[id] = {
      // Assign properties individually so we don't end up writing extra
      // properties to storage.
      prereq: dependency.prereq,
      dependent: dependency.dependent,
      createdAt: dateToNumber(dependency.createdAt),
      deletedAt: dependency.deletedAt === null ? undefined : dateToNumber(dependency.deletedAt)
    }
  }

  const encoded: z.infer<typeof root> = {
    version: 1,
    dependencies,
    tasks
  }

  // The conversion above should catch any encoding issues as type issues, but
  // if I've really messed something up, this will let me know if we wrote an
  // invalid file sooner rather than later.
  root.parse(encoded)

  // If there's a type error on the following line, one of the types in the
  // persisted schema isn't a valid JSON type. (e.g. Date)
  return JSON.stringify(encoded as JsonAny)
}
